import { type JSX, useState } from 'react'

import { Link, useLocation, useNavigate } from 'react-router-dom'

import { cn } from '@/lib/utils'

import { buttonVariants } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

interface SidebarNavProps extends React.HTMLAttributes<HTMLElement> {
  items: {
    href: string
    title: string
    icon: JSX.Element
  }[]
}

export default function SidebarNav({ className, items, ...props }: SidebarNavProps) {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const [val, setVal] = useState(pathname ?? '/profile')

  const handleSelect = (e: string) => {
    setVal(e)
    navigate(e)
  }

  return (
    <>
      {/* Mobile Select Dropdown */}
      <div className='p-2 md:hidden'>
        <Select value={val} onValueChange={handleSelect}>
          <SelectTrigger className='h-12 w-full'>
            <SelectValue placeholder='Select section'>
              <div className='flex items-center gap-2'>
                {items.find((item) => item.href === val)?.icon}
                <span>{items.find((item) => item.href === val)?.title || 'Select section'}</span>
              </div>
            </SelectValue>
          </SelectTrigger>
          <SelectContent className='min-w-[200px]'>
            {items.map((item) => (
              <SelectItem key={item.href} value={item.href} className='py-3'>
                <div className='flex items-center gap-3'>
                  <span className='text-primary'>{item.icon}</span>
                  <span className='text-sm font-medium'>{item.title}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Desktop Sidebar */}
      <ScrollArea type='always' className='hidden h-full md:block'>
        <nav className={cn('flex flex-col space-y-1 p-2', className)} {...props}>
          {items.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              className={cn(
                buttonVariants({ variant: 'ghost' }),
                'h-auto justify-start rounded-lg px-4 py-3',
                'transition-all',
                pathname === item.href
                  ? 'text-primary bg-blue-50 font-semibold'
                  : 'hover:text-primary text-gray-500 hover:bg-gray-100',
                'group'
              )}
            >
              <span
                className={cn(
                  'mr-3',
                  pathname === item.href ? 'text-primary' : 'text-muted-foreground group-hover:text-primary'
                )}
              >
                {item.icon}
              </span>
              <span className='text-sm'>{item.title}</span>
              <span
                className={cn(
                  'bg-primary ml-auto h-full w-1 rounded-full',
                  'transition-all duration-300',
                  pathname === item.href ? 'opacity-100' : 'opacity-0'
                )}
              />
            </Link>
          ))}
        </nav>
      </ScrollArea>
    </>
  )
}
